import { useEffect, useState, useCallback } from "react";
import { api } from "@/api/client";
import { agencyApi, apiErr } from "./agencyApi";
import { Lightbox, useThumbBlob } from "./Lightbox";
import { when } from "./AgencyBits";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";

// Recce — agency Project Detail tab (Wave R3+R4, 2026-10), between "Preferred crew" and "Cost
// sheet" (AgencyProjectDetail.jsx). Nothing renders here until the recce's status is Shared or
// Final (GET /agency/projects/{id}/recce — modules/build.py) — that's the server's own gate,
// this component just shows the empty state on a 404. Preview Mode (`user.is_preview`) is
// read-only client-side too (defense in depth; the real enforcement is deny_if_agency_preview
// on every write route).
const PREF_PILL = { "Preferred": "ag-pill-green", "Not preferred": "ag-pill-red" };

function ThumbImg({ projectId, photoId }) {
  const { objectUrl } = useThumbBlob(photoId ? agencyApi.recceThumbUrl(projectId, photoId) : null);
  return objectUrl ? <img src={objectUrl} alt="" /> : null;
}

// Recce Wave 2B (2026-10, RS-11/RA-02) — "The agency Recce opens on this Summary": counts
// (Locations/Approved/Backup, computed server-side by agency_get_recce) always show; each
// text box only renders if modules/build.py::recce_projection actually sent it (an empty box
// was never sent at all — never a visibility check done client-side). No AI generation.
const SUMMARY_BOXES = [
  { key: "needs_your_input", label: "Needs your input" },
  { key: "key_recommendations", label: "Key recommendations" },
  { key: "open_questions", label: "Open questions" },
  { key: "production_concerns", label: "Production concerns" },
];

function RecceSummary({ recce, counts }) {
  const boxes = SUMMARY_BOXES.filter(({ key }) => recce[key]);
  return (
    <div className="ag-card" style={{ marginBottom: 24 }} data-testid="agency-recce-summary">
      <div style={{ display: "flex", gap: 14, marginBottom: boxes.length ? 14 : 0, flexWrap: "wrap" }} className="ag-small" data-testid="agency-recce-summary-counts">
        <span data-testid="agency-recce-summary-count-locations">{counts?.locations ?? 0} Location{(counts?.locations ?? 0) === 1 ? "" : "s"}</span>
        <span>·</span>
        <span data-testid="agency-recce-summary-count-approved">{counts?.approved ?? 0} Approved</span>
        <span>·</span>
        <span data-testid="agency-recce-summary-count-backup">{counts?.backup ?? 0} Backup</span>
      </div>
      {boxes.map(({ key, label }) => (
        <div key={key} style={{ marginBottom: 10 }} data-testid={`agency-recce-summary-${key}`}>
          <div className="ag-label" style={{ marginBottom: 3 }}>{label}</div>
          <div className="ag-body">{recce[key]}</div>
        </div>
      ))}
    </div>
  );
}

// Recce Wave 2B (2026-10, RA-06 follow-up) — agency could previously only Discuss a Framing
// Option; Location and Recce-level threads (backend already supports both entity types, see
// modules/build.py's `_RECCE_ENTITY_COLLECTIONS`) had no agency-facing control at all.
function RecceDiscuss({ projectId, entityRef, readOnly, testIdPrefix }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginBottom: 20 }}>
      <button type="button" className="ag-link" onClick={() => setOpen((v) => !v)} data-testid={`${testIdPrefix}-discuss-toggle`}>
        <MessageCircle size={13} style={{ marginRight: 4, verticalAlign: "-2px" }} /> {open ? "Hide discussion" : "Discuss"}
      </button>
      {open && <RecceCommentThread projectId={projectId} entityRef={entityRef} readOnly={readOnly} />}
    </div>
  );
}

export function AgencyRecce({ projectId, onJumpToTab, initialRecceId }) {
  const { user } = useAuth();
  const readOnly = !!user?.is_preview;
  // L4 (Cursor review, 2026-10) — every Shared/Final recce on this project, for the switcher
  // below (only rendered when there's more than one). `selectedRecceId` null = let the server
  // pick its own default ("most recently shared"); `initialRecceId` (a notification deep-link)
  // pre-selects one specific recce on first mount.
  const [recceList, setRecceList] = useState([]);
  const [selectedRecceId, setSelectedRecceId] = useState(initialRecceId || null);
  const [overview, setOverview] = useState(null); // null=loading | {notShared:true} | {recce, locations, needs_input, shoot_plan}
  const [locationId, setLocationId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [openIndex, setOpenIndex] = useState(null);
  const [pdfVersions, setPdfVersions] = useState(null);

  useEffect(() => { agencyApi.recces(projectId).then((r) => setRecceList(r.data.items || [])).catch(() => setRecceList([])); }, [projectId]);

  const loadOverview = useCallback(() => {
    agencyApi.recce(projectId, selectedRecceId).then((r) => setOverview(r.data)).catch(() => setOverview({ notShared: true }));
    agencyApi.recceePdfVersions(projectId, selectedRecceId).then((r) => setPdfVersions(r.data.items || [])).catch(() => setPdfVersions([]));
  }, [projectId, selectedRecceId]);
  useEffect(() => { loadOverview(); }, [loadOverview]);

  const loadDetail = useCallback((locId) => {
    agencyApi.recceLocation(projectId, locId, selectedRecceId).then((r) => setDetail(r.data)).catch(() => setDetail(null));
  }, [projectId, selectedRecceId]);
  useEffect(() => { if (locationId) loadDetail(locationId); }, [locationId, loadDetail]);

  if (overview === null) return null;

  if (overview.notShared) {
    return (
      <div className="ag-section" data-testid="agency-recce-section">
        <h3 className="ag-h3" style={{ marginBottom: 16 }}>Recce</h3>
        <div className="ag-empty" data-testid="agency-recce-not-shared">NJM hasn't shared a location recce for this project yet.</div>
      </div>
    );
  }

  if (locationId) {
    return (
      <LocationDetail projectId={projectId} detail={detail} readOnly={readOnly} openIndex={openIndex} onOpenLightbox={setOpenIndex}
        onBack={() => { setLocationId(null); setDetail(null); setOpenIndex(null); }} onChanged={() => loadDetail(locationId)} />
    );
  }

  const { recce, locations, needs_input } = overview;
  return (
    <div className="ag-section" data-testid="agency-recce-section">
      {recceList.length > 1 && (
        <div className="ag-recce-switcher" data-testid="agency-recce-switcher" style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          {recceList.map((r) => (
            <button key={r.id} className={`ag-pill ${r.id === recce.id ? "ag-pill-gold" : "ag-pill-neutral"}`}
              style={{ cursor: "pointer", border: "none" }}
              onClick={() => { setSelectedRecceId(r.id); setLocationId(null); }} data-testid={`agency-recce-switch-${r.id}`}>
              {r.title}
            </button>
          ))}
        </div>
      )}
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 6 }}>
        <h3 className="ag-h3">{recce.title}</h3>
        <span className="ag-pill ag-pill-neutral" data-testid="agency-recce-status">{recce.status}</span>
      </div>
      {(recce.city_area || (recce.visit_dates || []).length > 0) && (
        <div className="ag-small" style={{ marginBottom: 20 }}>
          {recce.city_area}{(recce.visit_dates || []).length ? ` · ${recce.visit_dates.join(", ")}` : ""}
        </div>
      )}

      <RecceSummary recce={recce} counts={overview.counts} />

      {needs_input.length > 0 && (
        <div style={{ marginBottom: 28 }} data-testid="agency-recce-needs-input">
          <div className="ag-label" style={{ marginBottom: 10 }}>Needs your input</div>
          {needs_input.map((n, i) => (
            <div key={i} className="ag-recce-needs-row" data-testid={`agency-recce-needs-input-row-${i}`}
              onClick={() => (n.type === "open_item" ? onJumpToTab?.("Open items") : setLocationId(n.location_id))}>
              <span className="ag-body">{n.type === "framing_option" ? `${n.location_name} — ${n.option_title}` : n.title}</span>
              <span className="ag-link">{n.type === "open_item" ? "Open items →" : "Review →"}</span>
            </div>
          ))}
        </div>
      )}

      {locations.length === 0 ? (
        <div className="ag-empty">No locations yet.</div>
      ) : (
        <div className="ag-recce-loc-grid">
          {locations.map((loc) => (
            <button key={loc.id} className="ag-recce-loc-card" onClick={() => setLocationId(loc.id)} data-testid={`agency-recce-location-card-${loc.id}`}>
              <div className="ag-recce-loc-thumb"><ThumbImg projectId={projectId} photoId={loc.cover_photo_id} /></div>
              <div className="ag-recce-loc-body">
                <div className="ag-recce-loc-name">{loc.name}</div>
                <div className="ag-recce-loc-meta">
                  <span>{loc.photo_count} photos</span>
                  {loc.decision && loc.decision !== "TBC" && <span className="ag-pill ag-pill-neutral">{loc.decision}</span>}
                  {loc.agency_preference && <span className={`ag-pill ${PREF_PILL[loc.agency_preference] || "ag-pill-neutral"}`}>{loc.agency_preference}</span>}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {overview.schedule_days?.length > 0 && <ScheduleTable days={overview.schedule_days} rows={overview.shoot_plan || []} locations={locations} />}
      <RecceDiscuss projectId={projectId} entityRef={{ type: "recce", id: recce.id }} readOnly={readOnly} testIdPrefix="agency-recce" />
      <PdfReports projectId={projectId} recceId={recce.id} versions={pdfVersions} />
    </div>
  );
}

// Wave 3 (2026-10) — read-only, grouped by Shoot Day (replaces the old flat day_label/time
// list) — a `needs_review` day never reaches here at all (filtered server-side), nor does a
// row with `notes_visibility: "internal"`'s own notes text (stripped server-side too).
function ScheduleTable({ days, rows, locations }) {
  const nameById = Object.fromEntries(locations.map((l) => [l.id, l.name]));
  const rowsByDay = {};
  for (const r of rows) (rowsByDay[r.shoot_day_id] = rowsByDay[r.shoot_day_id] || []).push(r);
  const sortedDays = [...days].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  return (
    <div style={{ marginTop: 28 }} data-testid="agency-recce-schedule">
      <div className="ag-label" style={{ marginBottom: 10 }}>Production schedule</div>
      {sortedDays.map((day) => {
        const dayRows = (rowsByDay[day.id] || []).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        if (dayRows.length === 0) return null;
        return (
          <div key={day.id} className="ag-card" style={{ marginBottom: 14 }} data-testid={`agency-recce-schedule-day-${day.id}`}>
            <div className="ag-body" style={{ fontWeight: 600, marginBottom: 6 }}>
              {day.label}{day.date ? ` — ${new Date(day.date).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}` : ""}
            </div>
            {dayRows.map((r, i) => (
              <div key={r.id} style={{ display: "flex", gap: 14, padding: "9px 0", borderBottom: i < dayRows.length - 1 ? "1px solid var(--line)" : "none" }} data-testid={`agency-recce-schedule-row-${r.id}`}>
                <span className="ag-small" style={{ width: 90 }}>{r.start || "—"}{r.end ? ` – ${r.end}` : ""}</span>
                <span className="ag-body" style={{ flex: 1 }}>
                  {r.key_milestone && <span className="ag-pill ag-pill-neutral" style={{ marginRight: 6 }} data-testid={`agency-recce-schedule-milestone-${r.id}`}>Milestone</span>}
                  {r.activity}{r.location_id && nameById[r.location_id] ? ` — ${nameById[r.location_id]}` : (r.location_other ? ` — ${r.location_other}` : "")}
                  {r.people_subject ? ` (${r.people_subject})` : ""}
                </span>
                {r.notes && <span className="ag-small">{r.notes}</span>}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function PdfReports({ projectId, recceId, versions }) {
  if (!versions || versions.length === 0) return null;
  // Recce Wave 2A (2026-10, RA-08/09/10 root cause) — same popup-blocker fix as
  // PdfPublishPanel.jsx's staff View: `window.open` after an `await` is silently blocked by
  // most browsers, which is why "View" visibly did nothing. Open the tab synchronously first.
  const view = (versionId) => {
    const tab = window.open("", "_blank");
    if (!tab) { toast.error("Your browser blocked the new tab — allow pop-ups and try again."); return; }
    api.get(agencyApi.recceePdfFileUrl(projectId, versionId, recceId), { responseType: "blob" })
      .then((r) => { tab.location.href = window.URL.createObjectURL(r.data); })
      .catch((e) => { tab.close(); toast.error(apiErr(e)); });
  };
  const download = async (versionId, versionLabel) => {
    try {
      const r = await api.get(agencyApi.recceePdfFileUrl(projectId, versionId, recceId), { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${versionLabel || "recce"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div style={{ marginTop: 28 }} data-testid="agency-recce-pdf-reports">
      <div className="ag-label" style={{ marginBottom: 10 }}>Reports</div>
      <div className="ag-card">
        {versions.map((v, i) => (
          <div key={v.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: i < versions.length - 1 ? "1px solid var(--line)" : "none" }} data-testid={`agency-recce-pdf-row-${v.id}`}>
            <div>
              <div className="ag-body" style={{ fontWeight: 600 }}>Recce report · {v.version}</div>
              {v.created_at && <div className="ag-small">Published {new Date(v.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</div>}
            </div>
            <div style={{ display: "flex", gap: 14 }}>
              <button type="button" className="ag-link" onClick={() => view(v.id)} data-testid={`agency-recce-pdf-view-${v.id}`}>View →</button>
              <button type="button" className="ag-link" onClick={() => download(v.id, v.version)} data-testid={`agency-recce-pdf-download-${v.id}`} aria-label={`Download ${v.version}`}>Download</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LocationDetail({ projectId, detail, readOnly, onBack, onOpenLightbox, openIndex, onChanged }) {
  if (!detail) {
    return (
      <div className="ag-section">
        <button className="ag-link" onClick={onBack} data-testid="agency-recce-back-btn">← All locations</button>
      </div>
    );
  }
  const { location, photos, framing_options } = detail;
  const photoItems = photos.map((p) => ({ id: p.id, caption: p.caption, url: agencyApi.recceWebUrl(projectId, p.id) }));

  const setPreference = async (value) => {
    try { await agencyApi.setLocationPreference(projectId, location.id, value === location.agency_preference ? null : value); onChanged(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="ag-section" data-testid="agency-recce-location-detail">
      <button className="ag-link" onClick={onBack} data-testid="agency-recce-back-btn" style={{ display: "block", marginBottom: 18 }}>← All locations</button>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 6 }}>
        <h3 className="ag-h3">{location.name}</h3>
        {location.decision && location.decision !== "TBC" && <span className="ag-pill ag-pill-neutral">{location.decision}</span>}
      </div>
      {location.address_area && <div className="ag-small" style={{ marginBottom: 20 }}>{location.address_area}</div>}

      <div className="ag-stack" style={{ marginBottom: 24 }}>
        <div className="ag-label">Your preference</div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className={`ag-btn ${location.agency_preference === "Preferred" ? "ag-btn-gold" : "ag-btn-ghost"}`} disabled={readOnly}
            onClick={() => setPreference("Preferred")} data-testid="agency-recce-location-pref-preferred">Preferred</button>
          <button className={`ag-btn ${location.agency_preference === "Not preferred" ? "ag-btn-dark" : "ag-btn-ghost"}`} disabled={readOnly}
            onClick={() => setPreference("Not preferred")} data-testid="agency-recce-location-pref-not-preferred">Not preferred</button>
        </div>
      </div>

      {Object.keys(location.notes || {}).length > 0 && (
        <div style={{ marginBottom: 24 }}>
          {Object.entries(location.notes).map(([key, block]) => (
            <div key={key} className="ag-recce-note-block" data-testid={`agency-recce-note-${key}`}>
              <div className="ag-label" style={{ marginBottom: 4 }}>{key.replace(/_/g, " ")}</div>
              {Object.entries(block).map(([f, v]) => <div key={f} className="ag-body">{v}</div>)}
            </div>
          ))}
        </div>
      )}

      {photoItems.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <div className="ag-label" style={{ marginBottom: 10 }}>Photos</div>
          <div className="ag-bts-grid">
            {photoItems.map((item, i) => (
              <button key={item.id} className="ag-bts-thumb" onClick={() => onOpenLightbox(i)} data-testid={`agency-recce-photo-thumb-${item.id}`}>
                <ThumbImg projectId={projectId} photoId={item.id} />
              </button>
            ))}
          </div>
          {openIndex !== null && <Lightbox items={photoItems} index={openIndex} onClose={() => onOpenLightbox(null)} onNavigate={onOpenLightbox} />}
        </div>
      )}

      {framing_options.length > 0 && (
        <div>
          <div className="ag-label" style={{ marginBottom: 10 }}>Framing options</div>
          {framing_options.map((opt, i) => (
            <FramingOptionCard key={opt.id} projectId={projectId} option={opt} index={i} readOnly={readOnly} onChanged={onChanged} />
          ))}
        </div>
      )}

      <RecceDiscuss projectId={projectId} entityRef={{ type: "recce_location", id: location.id }} readOnly={readOnly} testIdPrefix="agency-recce-location" />
    </div>
  );
}

function FramingOptionCard({ projectId, option, index, readOnly, onChanged }) {
  const [showDiscuss, setShowDiscuss] = useState(false);

  const setResponse = async (value) => {
    try { await agencyApi.setFramingResponse(projectId, option.id, value === option.agency_response ? null : value); onChanged(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="ag-recce-option" data-testid={`agency-recce-framing-option-${option.id}`}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 8 }}>
        <span className="ag-pill ag-pill-neutral">OPTION {index + 1}</span>
        <span className="ag-body" style={{ fontWeight: 600 }}>{option.title}</span>
      </div>
      {option.njm_note && <div className="ag-body" style={{ marginBottom: 12 }}>{option.njm_note}</div>}
      {option.photo_ids.length > 0 && (
        <div className="ag-bts-grid" style={{ gridTemplateColumns: "repeat(6, 1fr)", marginBottom: 12 }}>
          {option.photo_ids.map((pid) => (
            <div key={pid} className="ag-bts-thumb" style={{ cursor: "default" }} data-testid={`agency-recce-framing-photo-${pid}`}>
              <ThumbImg projectId={projectId} photoId={pid} />
            </div>
          ))}
        </div>
      )}
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <button className={`ag-btn ${option.agency_response === "Preferred" ? "ag-btn-gold" : "ag-btn-ghost"}`} disabled={readOnly}
          onClick={() => setResponse("Preferred")} data-testid={`agency-recce-framing-preferred-${option.id}`}>Preferred</button>
        <button className={`ag-btn ${option.agency_response === "Not preferred" ? "ag-btn-dark" : "ag-btn-ghost"}`} disabled={readOnly}
          onClick={() => setResponse("Not preferred")} data-testid={`agency-recce-framing-not-preferred-${option.id}`}>Not preferred</button>
        <button className="ag-link" onClick={() => setShowDiscuss((v) => !v)} data-testid={`agency-recce-framing-discuss-${option.id}`}>
          <MessageCircle size={13} style={{ marginRight: 4, verticalAlign: "-2px" }} /> Discuss
        </button>
      </div>
      {showDiscuss && <RecceCommentThread projectId={projectId} entityRef={{ type: "recce_framing_option", id: option.id }} readOnly={readOnly} />}
    </div>
  );
}

function RecceCommentThread({ projectId, entityRef, readOnly }) {
  const [comments, setComments] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    agencyApi.comments(projectId, { type: entityRef.type, id: entityRef.id }).then((r) => setComments(r.data.items || [])).catch(() => setComments([]));
  }, [projectId, entityRef.type, entityRef.id]);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try { await agencyApi.addComment(projectId, text.trim(), entityRef); setText(""); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setSending(false); }
  };

  if (comments === null) return null;
  return (
    <div className="ag-comment-thread" data-testid="agency-recce-comment-thread">
      {comments.length === 0 && <div className="ag-small">No messages yet.</div>}
      {comments.map((c) => (
        <div key={c.id} className="ag-comment-row" data-testid={`agency-recce-comment-${c.id}`}>
          <span className="ag-comment-author">{c.author_name}</span><span className="ag-comment-at">{when(c.at)}</span>
          <div className="ag-comment-text">{c.text}</div>
        </div>
      ))}
      {!readOnly && (
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <input className="ag-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a reply…"
            data-testid="agency-recce-comment-input" onKeyDown={(e) => e.key === "Enter" && send()} />
          <button className="ag-btn ag-btn-dark" disabled={sending || !text.trim()} onClick={send} data-testid="agency-recce-comment-send">Send</button>
        </div>
      )}
    </div>
  );
}
